import React, { useState } from 'react'
import Checkbox from '@material-ui/core/Checkbox'
import Dialog from 'components/dialog'
import MuiDialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Avatar from '@material-ui/core/Avatar'
import { makeStyles } from '@material-ui/core/styles'
import { getInitials } from 'lib/text-display'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from 'components/material-symbol/icons/Close'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import pluralize from 'lib/pluralize'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import { isDistinctChannel } from '../utils'

const useStyles = makeStyles(theme => ({
  avatarStyles: {
    color: theme.palette.primary.contrastText,
    marginRight: theme.spacing(1),
  },
  dialogContent: {
    paddingLeft: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  itemText: {
    flex: '0 0 auto',
    [theme.breakpoints.only('xs')]: {
      flex: '1 1 auto',
    },
    marginRight: theme.spacing(1),
  },
}))

const MembersModal = ({ channel, trigger, currentUserId }) => {
  const styles = useStyles()
  const [checkedMembers, setCheckedMembers] = useState([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)

  const isChatOwner = channel?.data.created_by.id === currentUserId
  const membersArray = Object.values(channel.state?.members || {})
  const showRemoveMembers =
    !isDistinctChannel(channel) && membersArray.length > 2 && isChatOwner

  const handleToggle = id => {
    setCheckedMembers(prev =>
      prev.includes(id) ? prev.filter(mid => mid !== id) : [...prev, id],
    )
  }

  const handleDelete = async closeDialog => {
    try {
      await channel.removeMembers(checkedMembers)
    } catch (e) {
      snackbarError('Failed to remove members')
      window.rg4js &&
        window.rg4js('send', {
          error: e,
          tags: ['messaging', 'stream'],
        })
      return null
    }
    setCheckedMembers([])
    setConfirmOpen(false)
    closeDialog()
  }

  return (
    <Dialog trigger={trigger} fullWidth maxWidth="sm">
      {({ closeDialog }) => (
        <>
          <DialogTitle disableTypography style={{ paddingLeft: '32px' }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Members</Typography>
              <IconButton onClick={closeDialog}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers className={styles.dialogContent}>
            {membersArray.map(member => (
              <ListItem key={member.user.id}>
                <Avatar
                  aria-label="member"
                  src={member.user?.image}
                  className={styles.avatarStyles}
                >
                  {!member.user?.image && getInitials(member.user.name)}
                </Avatar>
                <ListItemText
                  primary={member.user.name}
                  classes={{ root: styles.itemText }}
                />
                {member.user.id === currentUserId && (
                  <ListItemText secondary="(You)" />
                )}
                {showRemoveMembers && (
                  <Box ml="auto">
                    <Checkbox
                      edge="end"
                      checked={checkedMembers.includes(member.user.id)}
                      onChange={() => handleToggle(member.user.id)}
                      disabled={member.user.id === currentUserId}
                    />
                  </Box>
                )}
              </ListItem>
            ))}
          </DialogContent>
          {showRemoveMembers && (
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <DialogActions>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={checkedMembers.length === 0}
                  onClick={() => setConfirmOpen(true)}
                >
                  {`Delete ${
                    checkedMembers.length > 0 ? checkedMembers.length : ''
                  }  
                  ${pluralize(checkedMembers.length, 'Member')}`}
                </Button>
              </DialogActions>
            </Box>
          )}
          <MuiDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            maxWidth="xs"
          >
            <DialogTitle>Delete Members</DialogTitle>
            <DialogContent>
              <DialogContent>
                {`Are you sure want to remove ${
                  checkedMembers.length
                } ${pluralize(
                  checkedMembers.length,
                  'member',
                )} from the group?`}
              </DialogContent>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)} color="primary">
                Cancel
              </Button>
              <Button
                onClick={() => handleDelete(closeDialog)}
                variant="contained"
                startIcon={<DeleteOutlinedIcon />}
                style={{ backgroundColor: '#d32f2f', color: '#fff' }}
              >
                Remove
              </Button>
            </DialogActions>
          </MuiDialog>
        </>
      )}
    </Dialog>
  )
}

export default MembersModal
